import React, { useEffect, useRef, useContext } from "react";
import { Form, Button } from "react-bootstrap";
import Select from "react-select";
import {
    SidebarView,
    SidebarContext,
    SidebarContextProvider,
    useSyncedField,
    useSidebarField,
} from "../../components/Sidebar";
import { longDateString } from "../../utils";

function AddZoneSidebarContent(props) {
    const { plantId, parentOptions = [], deviceOptions = [] } = props;

    const { entity } = useContext(SidebarContext);

    const [zoneName, setZoneName] = useSyncedField("name", "");
    const [zoneDesc, setZoneDesc] = useSyncedField("description", "");
    const [parentOpt, setParentOpt] = useSyncedField("parent", null);
    const [selectedDevices, setSelectedDevices] = useSyncedField("devices", []);

    // Runtime-only field for tracking initial device state
    const [initialDeviceIds, setInitialDeviceIds] = useSidebarField(
        "initialDeviceIds",
        new Set()
    );

    const basicFormRef = useRef(null);

    // Add --None-- option to parent zones
    const parentOptionsWithNone = [
        { value: null, label: "--None--" },
        ...parentOptions,
    ];

    // Track if we've initialized this entity already to prevent re-initialization during save
    const entityIdRef = useRef(null);

    // Initialize fields from entity when it changes
    useEffect(() => {
        const currentEntityId = entity?.id || null;

        // Only reinitialize if the entity ID actually changed
        if (
            entityIdRef.current === currentEntityId &&
            currentEntityId !== null
        ) {
            return;
        }
        entityIdRef.current = currentEntityId;

        if (entity) {
            setZoneName(entity.name || "");
            setZoneDesc(entity.description || "");

            // Find parent zone option
            const currentParentId = entity.parent?.id ?? entity.parent_id;
            const found = currentParentId
                ? parentOptions.find(
                      (o) => Number(o.value) === Number(currentParentId)
                  )
                : { value: null, label: "--None--" };
            setParentOpt(found || { value: null, label: "--None--" });

            // Initialize selected devices
            if (entity.installed_devices) {
                const byId = new Map(
                    (deviceOptions || []).map((o) => [String(o.value), o])
                );
                const deviceIds = new Set(
                    entity.installed_devices.map((d) => String(d.id))
                );
                setInitialDeviceIds(deviceIds);

                const prefilled = entity.installed_devices.map((d) => {
                    const hit = byId.get(String(d.id));
                    return hit || { value: d.id, label: d.name || d.id };
                });
                setSelectedDevices(prefilled);
            } else {
                setInitialDeviceIds(new Set());
                setSelectedDevices([]);
            }
        } else {
            // Reset for new zone
            setZoneName("");
            setZoneDesc("");
            setParentOpt(
                parentOptions.find((o) => Number(o.value) === 1) || null
            );
            setSelectedDevices([]);
            setInitialDeviceIds(new Set());
        }
    }, [entity?.id, parentOptions, deviceOptions]);

    return (
        <SidebarView title="Zone">
            <SidebarView.Page name="basic_details" title="Basic Details">
                <Form
                    ref={basicFormRef}
                    className="d-flex flex-column h-100"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <Form.Group className="mb-3">
                        <Form.Label>Parent Zone</Form.Label>
                        <Select
                            inputId="parent_zones"
                            options={parentOptionsWithNone}
                            value={parentOpt}
                            onChange={(opt) => setParentOpt(opt)}
                            placeholder="Select a zone"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter here"
                            value={zoneName}
                            onChange={(e) => setZoneName(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Max allowed characters: 120"
                            value={zoneDesc}
                            onChange={(e) => setZoneDesc(e.target.value)}
                        />
                    </Form.Group>
                </Form>
            </SidebarView.Page>

            <SidebarView.Page name="devices" title="Devices">
                <Form className="d-flex flex-column h-100">
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Devices <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                            options={deviceOptions}
                            placeholder="Select devices"
                            isMulti
                            value={selectedDevices}
                            onChange={(val) => setSelectedDevices(val || [])}
                            components={{ MultiValue: () => null }}
                            closeMenuOnSelect={true}
                            hideSelectedOptions={true}
                        />
                    </Form.Group>

                    {selectedDevices.length > 0 && (
                        <>
                            <div className="mb-2 fw-semibold">
                                Linked Devices
                            </div>
                            <div
                                className="d-flex flex-column"
                                style={{ gap: 10 }}
                            >
                                {selectedDevices.map((d) => {
                                    const isNew = !initialDeviceIds.has(
                                        String(d.value)
                                    );
                                    return (
                                        <div
                                            key={d.value}
                                            className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
                                        >
                                            <div
                                                className="d-flex align-items-center"
                                                style={{ gap: 10 }}
                                            >
                                                <span className="badge bg-light text-dark">
                                                    <i
                                                        className="fa fa-bolt"
                                                        aria-hidden="true"
                                                    />
                                                </span>

                                                {isNew && (
                                                    <span className="badge bg-success text-uppercase">
                                                        NEW
                                                    </span>
                                                )}

                                                <span className="text-truncate">
                                                    {d.label}
                                                </span>
                                            </div>

                                            <button
                                                type="button"
                                                className="btn btn-link text-danger p-0"
                                                onClick={() =>
                                                    setSelectedDevices((prev) =>
                                                        prev.filter(
                                                            (x) =>
                                                                x.value !==
                                                                d.value
                                                        )
                                                    )
                                                }
                                                title="Unlink"
                                                aria-label={`Unlink ${d.label}`}
                                            >
                                                <i
                                                    className="fa fa-unlink"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </Form>
            </SidebarView.Page>
        </SidebarView>
    );
}

function AddZoneSidebar(props) {
    const {
        show,
        onDiscard,
        item = null,
        plantId,
        parentOptions = [],
        deviceOptions = [],
        onSave = async () => {},
        onDelete = async () => {},
    } = props;

    const handleSaveInternal = async (emittedData, entity) => {
        if (!emittedData.name?.trim() || !plantId) return;

        // Handle parent zone selection
        let parentPayload = {};
        if (emittedData.parent !== undefined) {
            if (emittedData.parent?.value === null) {
                // User selected "--None--"
                parentPayload = { parent_id: null };
            } else if (emittedData.parent?.value != null) {
                // User selected a specific parent zone
                parentPayload = { parent_id: Number(emittedData.parent.value) };
            }
        }

        const payload = {
            id: entity?.id,
            name: emittedData.name.trim(),
            description: emittedData.description || "",
            plant_id: Number(plantId),
            ...parentPayload,
            is_active: true,
            selected_device_ids: (emittedData.devices || [])
                .map((d) => Number(d?.value))
                .filter(Number.isFinite),
        };

        await onSave(payload);
        // Parent (Zones.jsx) handles closing via handleClose callback
    };

    // Format metadata for display
    const getMetadata = () => {
        if (!item) return null;

        // Handle updated_by (could be object or string)
        const updatedBy =
            typeof item.updated_by === "object"
                ? item.updated_by?.name ||
                  item.updated_by?.username ||
                  item.updated_by?.email ||
                  "Unknown"
                : item.updated_by || "Unknown";

        const updatedDate = item.updated_at
            ? longDateString(item.updated_at)
            : "";

        return (
            <div className="d-flex gap-3">
                <small className="text-muted">
                    <i className="fas fa-user me-1"></i>
                    {updatedBy}
                </small>
                {updatedDate && (
                    <small className="text-muted">
                        <i className="fas fa-clock me-1"></i>
                        {updatedDate}
                    </small>
                )}
            </div>
        );
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={onDelete}
            onDiscard={onDiscard}
            initialPage="basic_details"
            metadata={getMetadata()}
        >
            <AddZoneSidebarContent
                plantId={plantId}
                parentOptions={parentOptions}
                deviceOptions={deviceOptions}
            />
        </SidebarContextProvider>
    );
}

export default AddZoneSidebar;
